import { inject } from '@angular/core';
import {
  CanActivateChildFn,
  CanActivateFn,
  CanLoadFn,
  UrlSegment
} from '@angular/router';
import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthStateService } from 'src/app/core/services/auth/auth-state.service';
import { MyWorldRedirectService } from 'src/app/core/services/auth/my-world-redirect.service';
import { ESession } from 'src/app/shared/enums/storage.enum';

import { ApiLiv } from '../../services/api/api-liv.service';
import { SessionService } from '../../services/session.service';

type AuthGuardDeps = {
  authStateService: AuthStateService;
  apiLiv: ApiLiv;
  sessionService: SessionService;
  myWorldRedirect: MyWorldRedirectService;
  i18n: I18nService;
};

function trackAutoLogin(deps: AuthGuardDeps): void {
  const autoLogin = deps.sessionService.get<string>(ESession.AutoLogin);

  if (autoLogin) {
    return;
  }

  deps.sessionService.save(ESession.AutoLogin, String(Date.now()));
  deps.apiLiv
    .post<void>('/Kibana/Login/SaveAutoLogin', null)
    .pipe(
      catchError(() => {
        deps.sessionService.delete(ESession.AutoLogin);
        return EMPTY;
      })
    )
    .subscribe();
}

function isAuthorized(
  attemptedUrl: string,
  deps: AuthGuardDeps
): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    const isLoggedIn = !!deps.authStateService.sessionSnapshot?.token;

    if (isLoggedIn) {
      if (deps.myWorldRedirect.tryHandleAuthenticatedAccess(attemptedUrl)) {
        return reject(false);
      }

      trackAutoLogin(deps);
      return resolve(true);
    }

    deps.myWorldRedirect.navigateToLogin(
      attemptedUrl,
      deps.i18n.currentLanguage()
    );

    return reject(false);
  });
}

function createAuthGuardDeps(): AuthGuardDeps {
  return {
    authStateService: inject(AuthStateService),
    apiLiv: inject(ApiLiv),
    sessionService: inject(SessionService),
    myWorldRedirect: inject(MyWorldRedirectService),
    i18n: inject(I18nService)
  };
}

export const authGuard: CanActivateFn = (_, state) => {
  return isAuthorized(state.url, createAuthGuardDeps());
};

export const authActivateChildGuard: CanActivateChildFn = (_, state) => {
  return isAuthorized(state.url, createAuthGuardDeps());
};

export const authLoadGuard: CanLoadFn = (_, segments) => {
  const deps = createAuthGuardDeps();
  const segmentPath = segments?.map((s: UrlSegment) => s.path).join('/');

  return isAuthorized(
    deps.myWorldRedirect.resolveAttemptedUrl(segmentPath),
    deps
  );
};
